import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type RevealPhase = 'idle' | 'pending' | 'revealed';

export interface UseScrollRevealOptions {
  /** 露出比例，默认 0.08（很高的区块也能及时触发） */
  threshold?: number;
  /** 视口内缩，默认底部内缩 8% */
  rootMargin?: string;
}

/**
 * react-snap 预渲染时不武装动效，避免静态快照里留下 opacity 为 0 的区块。
 * 它用 puppeteer 启动（默认带 --enable-automation），所以 webdriver 为 true。
 * 降低动效偏好由 mixin 里的 prefers-reduced-motion 处理，不在这里判断。
 */
function isPrerendering(): boolean {
  return window.navigator.webdriver;
}

/**
 * 滚动露出时播一次上滑入场。
 *
 * `ref` 挂在触发观察的元素上，`revealProps` 挂在实际做动效的元素上（可以是同一个），
 * 动效样式由 `mixins.scss` 的 `reveal-slide-up` 提供。
 */
export function useScrollReveal<T extends HTMLElement = HTMLElement>({
  threshold = 0.08,
  rootMargin = '0px 0px -8% 0px',
}: UseScrollRevealOptions = {}) {
  const ref = useRef<T>(null);
  const [phase, setPhase] = useState<RevealPhase>('idle');

  useLayoutEffect(() => {
    if (isPrerendering()) {
      return;
    }
    setPhase('pending');
  }, []);

  useEffect(() => {
    if (phase !== 'pending') {
      return;
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) {
          return;
        }
        setPhase('revealed');
        observer.disconnect();
      },
      { threshold, rootMargin },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [phase, threshold, rootMargin]);

  return {
    ref,
    phase,
    revealProps: { 'data-reveal': phase === 'idle' ? undefined : phase },
  };
}

export default useScrollReveal;
